import React, { useEffect, useState } from 'react';
import { Table, Pagination, Button, Modal } from 'react-bootstrap';
import {
  deleteInventoryIssuance,
  getInventoryIssuanceMetrics,
  getInventoryIssuancesPaginated,
  syncDeployed,
} from '../../services/project/inventoryService';
import IssuanceHistoryModal from '../../components/IssuanceHistoryModal';
import { toast } from 'react-toastify';
import InventoryQueryFilters from '../../components/InventoryQueryFilters';
import { useUsers } from '../../context/UserContext';
import { queueGIReport } from '../../services/project/reportService';

const getLocalDateInputValue = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

const toNumberOrNull = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const getDistributedValue = (item) => {
  return (
    toNumberOrNull(item?.totalDistributedQuantity) ??
    toNumberOrNull(item?.distributedQty) ??
    toNumberOrNull(item?.distributedQuantity) ??
    toNumberOrNull(item?.qtyDistributed)
  );
};

const getConsumedValue = (item) => {
  return (
    toNumberOrNull(item?.totalConsumedQuantity) ??
    toNumberOrNull(item?.consumedQty) ??
    toNumberOrNull(item?.consumedQuantity) ??
    toNumberOrNull(item?.qtyConsumed)
  );
};

const getRemainingValue = (item) => {
  const explicit =
    toNumberOrNull(item?.totalAvailableQuantity) ??
    toNumberOrNull(item?.remainingQty) ??
    toNumberOrNull(item?.remainingQuantity) ??
    toNumberOrNull(item?.availableQty);

  if (explicit !== null) return explicit;

  const distributed = getDistributedValue(item);
  const consumed = getConsumedValue(item);

  if (distributed !== null && consumed !== null) {
    return distributed - consumed;
  }

  return null;
};

const getIssuedValue = (item) => {
  return toNumberOrNull(item?.issuedQty);
};

const getMetricsRows = (metricsPayload) => {
  if (!metricsPayload) return [];

  const issuance = metricsPayload?.issuance;
  const candidates =
    (Array.isArray(metricsPayload?.items) && metricsPayload.items) ||
    (Array.isArray(issuance?.items) && issuance.items) ||
    (Array.isArray(issuance?.materials) && issuance.materials) ||
    (Array.isArray(metricsPayload?.metrics) && metricsPayload.metrics) ||
    (Array.isArray(issuance) && issuance) ||
    (Array.isArray(metricsPayload) && metricsPayload) ||
    [];

  return candidates;
};

const toMetricLookupKey = (entry) => {
  if (!entry) return '';
  return (
    entry?.issuanceItemId ||
    entry?.matCode ||
    entry?.materialCode ||
    entry?.matLabel ||
    entry?.code ||
    entry?.id ||
    ''
  );
};

const toMetricValue = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const getAggregateMetrics = (metricsPayload) => ({
  distributedTotal: toMetricValue(metricsPayload?.distributedTotal),
  consumedPersonal: toMetricValue(metricsPayload?.consumedPersonal),
  remaining: toMetricValue(metricsPayload?.remaining),
});

const InventoryTabContent = ({ projectId }) => {
  const { isSuperAdmin } = useUsers();
  const [deletingIssuanceId, setDeletingIssuanceId] = useState(null);
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [issuanceMetrics, setIssuanceMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    region: '',
    area: '',
    startDate: getLocalDateInputValue(),
    endDate: getLocalDateInputValue(),
  });
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedAreas, setExpandedAreas] = useState({});

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [projectId, page, size, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getInventoryIssuancesPaginated(projectId, {
        page,
        size,
        search: filters.search,
        region: filters.region,
        area: filters.area,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
      setItems(res?.data || []);
      setTotalPages(res?.totalPages || 1);
      setTotal(res?.total || 0);
    } catch (err) {
      setItems([]);
      setTotalPages(1);
      setTotal(0);
      toast.error('Failed to fetch inventory issuances');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInventory = async (id) => {
    if (!id) return;

    const confirmed = window.confirm(
      'Delete this issuance? This action cannot be undone.\n\n' +
      'This may fail if there are linked redistributions or consumption records.\n' +
      'Delete related distributions first before retrying.'
    );
    if (!confirmed) return;

    setDeletingIssuanceId(id);
    try {
      const res = await deleteInventoryIssuance(id);
      toast.success(res?.message || 'Inventory issuance deleted successfully');
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || 'Failed to delete inventory issuance');
    } finally {
      setDeletingIssuanceId(null);
    }
  };

  const handleSyncDeployed = async () => {
    setSyncLoading(true);
    try {
      const res = await syncDeployed(projectId, filters.startDate, filters.endDate);
      toast.success(res?.message || 'Deployed data synced successfully');
      await fetchData(); // Refresh the data
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || 'Failed to sync deployed data');
    } finally {
      setSyncLoading(false);
    }
  };

  const getPersonName = (person) => {
    if (!person) return '-';
    if (person.fullName) return person.fullName;
    const fullName = `${person.firstName || ''} ${person.lastName || ''}`.trim();
    return fullName || person.username || '-';
  };

  const getUserRole = (user) => user?.roleNames?.[0] || '-';

  const getUserRegionNames = (user) => {
    if (Array.isArray(user?.regionNames) && user.regionNames.length > 0) {
      return user.regionNames.filter(Boolean);
    }
    if (Array.isArray(user?.areas) && user.areas.length > 0) {
      return user.areas
        .map((a) => a?.regionName || a?.region?.name)
        .filter(Boolean);
    }
    return [];
  };

  const getUserAreaNames = (user) => {
    if (Array.isArray(user?.areaNames) && user.areaNames.length > 0) {
      return user.areaNames.filter(Boolean);
    }
    if (Array.isArray(user?.areas) && user.areas.length > 0) {
      return user.areas.map((a) => a?.name).filter(Boolean);
    }
    return [];
  };

  const toggleAreaExpansion = (id) => {
    setExpandedAreas((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const formatLocalDateTime = (dateTime) => {
    if (!dateTime) return '-';
    const parsed = new Date(dateTime);
    if (Number.isNaN(parsed.getTime())) return '-';
    const datePart = parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
    const timePart = parsed.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `${datePart} ${timePart}`;
  };

  const handleOpenItemsModal = (record) => {
    setSelectedRecord(record);
    setShowItemsModal(true);
  };

  useEffect(() => {
    const loadIssuanceMetrics = async () => {
      if (!showItemsModal || !selectedRecord?.GINumber) {
        setIssuanceMetrics(null);
        return;
      }

      setMetricsLoading(true);

      try {
        const res = await getInventoryIssuanceMetrics(selectedRecord.GINumber);
        setIssuanceMetrics(res || null);
      } catch (err) {
        setIssuanceMetrics(null);
        toast.error(err?.response?.data?.errorMessage || 'Failed to load issuance metrics');
      } finally {
        setMetricsLoading(false);
      }
    };

    loadIssuanceMetrics();
  }, [showItemsModal, selectedRecord?.GINumber]);

  const metricsRows = getMetricsRows(issuanceMetrics);
  const aggregateMetrics = getAggregateMetrics(issuanceMetrics);

  const metricsByKey = metricsRows.reduce((acc, row) => {
    const key = String(toMetricLookupKey(row)).trim();
    if (!key) return acc;
    acc[key] = row;
    return acc;
  }, {});

  const getMetricRowForItem = (item) => {
    const keys = [
      item?.issuanceItemId,
      item?.id,
      item?.matCode,
      item?.materialCode,
      item?.matLabel,
      item?.code,
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean);

    const matchedKey = keys.find((key) => metricsByKey[key]);
    return matchedKey ? metricsByKey[matchedKey] : null;
  };

  const getDistributedFromMetrics = (metricRow) =>
    toMetricValue(metricRow?.distributedTotal ?? metricRow?.totalDistributedQuantity);

  const getConsumedFromMetrics = (metricRow) =>
    toMetricValue(metricRow?.consumedPersonal ?? metricRow?.totalConsumedQuantity);

  const getRemainingFromMetrics = (metricRow) =>
    toMetricValue(metricRow?.remaining ?? metricRow?.totalAvailableQuantity);

  const getIssuedFromMetrics = (metricRow) =>
    toMetricValue(metricRow?.issuedQty);

  const handleApplyFilters = (nextFilters) => {
    setPage(0);
    setFilters(nextFilters);
  };

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    try {
      const res = await queueGIReport({
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
      toast.success(res?.message || 'GI report queued successfully');
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || 'Failed to queue GI report');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <>
      <h2 className="mb-3">Inventory</h2>

      <InventoryQueryFilters
        initialValues={filters}
        onApply={handleApplyFilters}
        isLoading={loading}
      />

      <div className="mb-3 d-flex gap-2">
        <Button
          variant="outline-primary"
          onClick={handleGenerateReport}
          disabled={isGeneratingReport}
        >
          {isGeneratingReport ? 'Queuing...' : 'Generate Report'}
        </Button>
        {isSuperAdmin && (
          <Button
            variant="outline-secondary"
            onClick={handleSyncDeployed}
            disabled={syncLoading}
          >
            {syncLoading ? 'Syncing...' : 'Sync deployed'}
          </Button>
        )}
      </div>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>GI Number</th>
            <th>GI Date</th>
            <th>Deployment Days</th>
            <th>User</th>
            <th>AIMS ID</th>
            <th>Role</th>
            <th>Region(s)</th>
            <th>Area(s)</th>
            <th>Created At</th>
            <th>Action</th>

          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={9}>Loading...</td></tr>
          ) : items.length === 0 ? (
            <tr><td colSpan={9}>No inventory found.</td></tr>
          ) : (
            items.map(item => {
              const user = item.user || {};
              const regionNames = Array.from(new Set(getUserRegionNames(user)));
              const areaNames = getUserAreaNames(user);
              const isExpanded = !!expandedAreas[item.id];
              const shouldCollapse = areaNames.length > 2;
              const visibleAreas = isExpanded || !shouldCollapse
                ? areaNames
                : areaNames.slice(0, 2);

              return (
                <tr key={item.id}>
                  <td>{item.GINumber ?? '-'}</td>
                  <td>{item.GIDate ?? '-'}</td>
                  <td>{item.deploymentDays ?? '-'} day(s)</td>
                  <td>{getPersonName(user)}</td>
                  <td>{user.aimsId || item.aimsId || '-'}</td>
                  <td>{getUserRole(user)}</td>
                  <td>{regionNames.length ? regionNames.join(', ') : '-'}</td>
                  <td>
                    {areaNames.length === 0 ? '-': (
                      <>
                        {visibleAreas.join(', ')}
                        {shouldCollapse && (
                          <>
                            {!isExpanded && ` +${areaNames.length - 2} more`}{' '}
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 align-baseline"
                              onClick={() => toggleAreaExpansion(item.id)}
                            >
                              {isExpanded ? 'Show less' : 'Show more'}
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </td>
                  <td>{formatLocalDateTime(item.createdAt)}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button variant="outline-primary" size="sm" onClick={() => handleOpenItemsModal(item)}>
                        View ({Array.isArray(item.items) ? item.items.length : 0})
                      </Button>
                      {isSuperAdmin && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={deletingIssuanceId === item.id}
                          onClick={() => handleDeleteInventory(item.id)}
                        >
                          {deletingIssuanceId === item.id ? 'Deleting...' : 'Delete'}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center">
        <div>Page: {page + 1} of {totalPages} (Total: {total})</div>
        <Pagination>
          <Pagination.Prev onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} />
          {[...Array(totalPages)].map((_, idx) => (
            <Pagination.Item key={idx} active={idx === page} onClick={() => setPage(idx)}>
              {idx + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} />
        </Pagination>
      </div>

      <Modal
        show={showItemsModal}
        onHide={() => {
          setShowItemsModal(false);
          setSelectedRecord(null);
        }}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Items {selectedRecord?.GINumber ? `- ${selectedRecord.GINumber}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2"><strong>User:</strong> {getPersonName(selectedRecord?.user)}</div>
          <div className="mb-2"><strong>Aims ID:</strong> {selectedRecord?.user?.aimsId || selectedRecord?.aimsId || '-'}</div>
          <div className="mb-2"><strong>Role:</strong> {getUserRole(selectedRecord?.user)}</div>
          <div className="mb-2"><strong>Region(s):</strong> {(() => {
            const names = Array.from(new Set(getUserRegionNames(selectedRecord?.user)));
            return names.length ? names.join(', ') : '-';
          })()}</div>
          <div className="mb-3"><strong>Area(s):</strong> {(() => {
            const names = getUserAreaNames(selectedRecord?.user);
            return names.length ? names.join(', ') : '-';
          })()}</div>

          {metricsLoading && <div className="text-muted small mb-3">Loading issuance metrics...</div>}

          {!metricsLoading && issuanceMetrics?.success && (
            <div className="mb-3">
              <strong>Issuance Metrics: </strong>
              <span className="me-3">Distributed: {aggregateMetrics.distributedTotal ?? '-'}</span>
              <span className="me-3">Consumed: {aggregateMetrics.consumedPersonal ?? '-'}</span>
              <span>Remaining: {aggregateMetrics.remaining ?? '-'}</span>
            </div>
          )}

          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>ID</th>
                <th>MatCode</th>
                <th>MatLabel</th>
                <th>MatType</th>
                <th>Description</th>
                <th>Issued</th>
                <th>Distributed</th>
                <th>Consumed</th>
                <th>Remaining</th>
              </tr>
            </thead>
            <tbody>
              {!Array.isArray(selectedRecord?.items) || selectedRecord.items.length === 0 ? (
                <tr><td colSpan={9}>No items found.</td></tr>
              ) : (
                selectedRecord.items.map((entry, idx) => {
                  const metricRow = getMetricRowForItem(entry);
                  const isSingleItemIssuance = (selectedRecord?.items?.length || 0) === 1;

                  const issued =
                    getIssuedFromMetrics(metricRow) ??
                    getIssuedValue(entry);

                  const distributed =
                    getDistributedFromMetrics(metricRow) ??
                    getDistributedValue(entry) ??
                    (isSingleItemIssuance ? aggregateMetrics.distributedTotal : null);
                  const consumed =
                    getConsumedFromMetrics(metricRow) ??
                    getConsumedValue(entry) ??
                    (isSingleItemIssuance ? aggregateMetrics.consumedPersonal : null);
                  const remaining =
                    getRemainingFromMetrics(metricRow) ??
                    getRemainingValue(entry) ??
                    (isSingleItemIssuance ? aggregateMetrics.remaining : null);

                  return (
                    <tr key={entry.id || idx}>
                      <td>{entry.id ?? '-'}</td>
                      <td>{entry.matCode ?? '-'}</td>
                      <td>{entry.matLabel ?? '-'}</td>
                      <td>{entry.matType ?? '-'}</td>
                      <td>{entry.matDescription ?? '-'}</td>
                      <td>{issued ?? '-'}</td>
                      <td>{distributed ?? '-'}</td>
                      <td>{consumed ?? '-'}</td>
                      <td>{remaining ?? '-'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-info"
            onClick={() => setShowHistoryModal(true)}
            disabled={!selectedRecord?.GINumber}
          >
            View Full History
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setShowItemsModal(false);
              setSelectedRecord(null);
              setIssuanceMetrics(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <IssuanceHistoryModal
        show={showHistoryModal}
        giNumber={selectedRecord?.GINumber}
        onHide={() => setShowHistoryModal(false)}
      />
    </>
  );
};

export default InventoryTabContent;
